/* ------------------------------------------------------------------ *
 * features/hr/StaffWorkspace.tsx
 *
 * A two-pane "work through everyone" view for the Contracts and
 * Compliance sections. Left: a persistent, searchable staff rail with a
 * per-person status hint. Right: the real ContractTab / ComplianceTab
 * (reused from StaffProfile — same editors, same save logic) for whoever
 * is selected, with Previous / Next so you can move down the list without
 * going back to a list screen each time.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Search, ChevronUp, ChevronDown, ExternalLink, UserRound } from 'lucide-react';
import { SoftCard } from '@/components/ui/glass';
import { useHrData } from '@/features/hr/useHrData';
import { ContractTab, ComplianceTab } from '@/features/hr/StaffProfile';
import {
  staffFullName, staffInitials, daysUntil,
  STAFF_CATEGORY_LABELS, summariseStaffCompliance,
  type HrStaff, type StaffCategory, type DocHealth,
} from '@/lib/hr';

type Mode = 'contract' | 'compliance';
type Props = { mode: Mode; onOpenStaff?: (staffId: string) => void };

const HEALTH_DOT: Record<DocHealth, string> = {
  ok: 'bg-emerald-400',
  expiring: 'bg-amber-400',
  expired: 'bg-red-500',
  missing: 'bg-gray-300 dark:bg-gray-600',
};

export default function StaffWorkspace({ mode, onOpenStaff }: Props) {
  const data = useHrData();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<StaffCategory | 'all'>('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.staff
      .filter((s) => !activeOnly || s.status === 'active')
      .filter((s) => category === 'all' || s.category === category)
      .filter((s) => !q || staffFullName(s).toLowerCase().includes(q) || s.employeeNo.toLowerCase().includes(q))
      .sort((a, b) => staffFullName(a).localeCompare(staffFullName(b)));
  }, [data.staff, activeOnly, category, query]);

  // keep a valid selection as the filter changes
  const selected: HrStaff | undefined = useMemo(() => {
    if (selectedId) { const hit = list.find((s) => s.id === selectedId); if (hit) return hit; }
    return list[0];
  }, [list, selectedId]);

  const selIndex = selected ? list.findIndex((s) => s.id === selected.id) : -1;
  const go = (delta: number) => {
    if (selIndex < 0) return;
    const next = list[selIndex + delta];
    if (next) setSelectedId(next.id);
  };

  // per-row status hint
  const hint = (s: HrStaff) => {
    if (mode === 'contract') {
      const c = data.contracts.find((x) => x.staffId === s.id && x.active);
      if (!c) return { text: 'No contract', cls: 'text-gray-400' };
      const d = daysUntil(c.endDate);
      if (d !== null && d >= 0 && d <= 90) return { text: `Ends in ${d}d`, cls: 'text-amber-600 dark:text-amber-400' };
      return { text: c.endDate ? 'Active' : 'Permanent', cls: 'text-gray-400' };
    }
    const sum = summariseStaffCompliance(s, data.complianceDocs, data.complianceMatrix);
    const dot = HEALTH_DOT[sum.worst] ?? HEALTH_DOT.ok;
    const label = sum.worst === 'expired' ? `${sum.expired} expired` : sum.worst === 'expiring' ? `${sum.expiring} expiring` : 'Up to date';
    return { dot, text: label, cls: sum.worst === 'expired' ? 'text-red-600 dark:text-red-400' : sum.worst === 'expiring' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400' };
  };

  // props for the reused tabs (mirrors StaffProfile's derivations)
  const myAllContracts = selected ? data.contracts.filter((c) => c.staffId === selected.id) : [];
  const myContract = myAllContracts.find((c) => c.active);
  const myContractHistory = myAllContracts.filter((c) => !c.active).sort((a, b) => b.startDate.localeCompare(a.startDate));
  const myPayroll = selected ? data.payrollRefs.find((p) => p.staffId === selected.id) : undefined;
  const myDocs = selected ? data.complianceDocs.filter((d) => d.staffId === selected.id) : [];

  return (
    <div className="grid lg:grid-cols-[20rem_1fr] gap-4">
      {/* ---- left rail ---- */}
      <SoftCard hover={false} >
        <div className="space-y-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or staff #"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2d5295]/30"
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as StaffCategory | 'all')}
              className="text-xs rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-1.5 flex-1 min-w-0"
            >
              <option value="all">All categories</option>
              {(Object.keys(STAFF_CATEGORY_LABELS) as StaffCategory[]).map((c) => (
                <option key={c} value={c}>{STAFF_CATEGORY_LABELS[c]}</option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 shrink-0">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} className="rounded" />
              Active
            </label>
          </div>

          <p className="text-[11px] text-gray-400">{list.length} staff</p>

          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1 divide-y divide-gray-50 dark:divide-gray-800">
            {list.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">No staff match.</p>
            ) : list.map((s) => {
              const h = hint(s);
              const isSel = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`w-full flex items-center gap-2.5 py-2.5 px-2 rounded-lg text-left transition-colors ${isSel ? 'bg-[#2d5295]/10 dark:bg-[#2d5295]/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${isSel ? 'bg-[#2d5295] text-white' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
                    {staffInitials(s)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{staffFullName(s)}</span>
                    <span className="block text-[11px] text-gray-400">#{s.employeeNo}</span>
                  </span>
                  <span className={`flex items-center gap-1 text-[11px] shrink-0 ${h.cls}`}>
                    {'dot' in h && h.dot && <span className={`w-2 h-2 rounded-full ${h.dot}`} />}
                    {h.text}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </SoftCard>

      {/* ---- right pane ---- */}
      <div className="space-y-4 min-w-0">
        {!selected ? (
          <SoftCard hover={false}>
            <div className="py-20 text-center text-gray-400">
              <UserRound size={32} className="mx-auto mb-2 opacity-40" />
              <p className="text-sm">Select a staff member to {mode === 'contract' ? 'update their contract' : 'review compliance'}.</p>
            </div>
          </SoftCard>
        ) : (
          <>
            <SoftCard hover={false}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h2 className="font-semibold text-gray-800 dark:text-gray-100 truncate">{staffFullName(selected)}</h2>
                  <p className="text-xs text-gray-400">#{selected.employeeNo} · {STAFF_CATEGORY_LABELS[selected.category]} · {selected.jobTitle}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {onOpenStaff && (
                    <button onClick={() => onOpenStaff(selected.id)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#2d5295] dark:text-gray-400">
                      <ExternalLink size={13} /> Full profile
                    </button>
                  )}
                  <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <button onClick={() => go(-1)} disabled={selIndex <= 0} className="px-2 py-1.5 disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800" title="Previous"><ChevronUp size={15} /></button>
                    <span className="px-2 text-[11px] text-gray-400 border-x border-gray-200 dark:border-gray-700">{selIndex + 1}/{list.length}</span>
                    <button onClick={() => go(1)} disabled={selIndex >= list.length - 1} className="px-2 py-1.5 disabled:opacity-30 hover:bg-gray-50 dark:hover:bg-gray-800" title="Next"><ChevronDown size={15} /></button>
                  </div>
                </div>
              </div>
            </SoftCard>

            {/* key forces the reused tab to remount cleanly on staff change */}
            {mode === 'contract' ? (
              <ContractTab
                key={selected.id}
                staff={selected}
                contract={myContract}
                history={myContractHistory}
                payroll={myPayroll}
              />
            ) : (
              <ComplianceTab key={selected.id} staff={selected} docs={myDocs} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
